import { useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner'
import { Loader2Icon, ScaleIcon } from 'lucide-react'

import { apiClient } from '@/api/client'
import { formatApiError } from '@/utils/httpErrors'
import {
  formatNumericInput, numericInputValue, parseNumericInput,
} from '@/lib/currency'
import {
  BALANCE_SHEET_FIELDS,
  type BalanceSheetCapturePeriod,
  type BalanceSheetField,
  type CaptureExtras,
} from '@/types/capture'
import type { FinancialYear } from '@/types/deal'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'

/**
 * FIELDS-1 P1a — balance-sheet intake, one column per fiscal period.
 *
 * compute_nwc_peg() has been shipped since v0.28.0 and has never once seen a
 * balance sheet: the handler fed it the P&L's parsed_data periods, which carry
 * no AR / inventory / AP / accruals at all, so the peg came back as a confident
 * $0 with a ±10% collar around nothing. There was no form anywhere in the
 * product that asked for these five figures. This is that form.
 *
 * Reported figures are preserved verbatim — nothing here is derived, rounded or
 * inferred. A blank field is stored as null and reads as "not entered yet",
 * which the peg reports as pending rather than scoring against an assumed zero.
 * Inventory is the one field where a real 0 is meaningful (a service business
 * genuinely holds none), so enter 0 there deliberately.
 */

const CY = new Date().getFullYear()
const FALLBACK_YEARS = [CY - 2, CY - 1, CY]

/** MIN_PERIODS in core/qoe/nwc_peg.py. Mirrored so the form can say how many
 *  more periods are needed before a peg exists, instead of silently saving
 *  two years and leaving the card pending with no explanation. */
const MIN_PERIODS = 3

const FIELD_LABELS: Record<BalanceSheetField, string> = {
  accounts_receivable: 'Accounts Receivable',
  inventory: 'Inventory',
  accounts_payable: 'Accounts Payable',
  accrued_liabilities: 'Accrued Liabilities',
  cash: 'Cash',
}

/** Which fields the peg actually consumes. `cash` is captured but is deliberately
 *  NOT in the NWC formula — say so, so nobody reads the peg as including it. It
 *  does not feed the bridge's working-capital true-up either; that is derived
 *  from net working capital vs the peg. */
const IN_PEG: Record<BalanceSheetField, boolean> = {
  accounts_receivable: true,
  inventory: true,
  accounts_payable: true,
  accrued_liabilities: true,
  cash: false,
}

interface NormalizationEnvelope {
  financial_years?: FinancialYear[]
  form_extras_json?: CaptureExtras | null
}

interface Props {
  dealId: string
  /** Called after a successful save so the caller can re-read the peg. */
  onSaved?: () => void
}

/** The fiscal years to collect: whatever the deal's P&L holds, so the balance
 *  sheet columns line up with the periods being normalized. A deal with no P&L
 *  yet falls back to the rolling window — never hardcoded years. */
function yearsFrom(rows: FinancialYear[] | undefined): number[] {
  const years = (rows ?? [])
    .map(r => r.year)
    .filter((y): y is number => typeof y === 'number')
  return years.length > 0 ? [...new Set(years)].sort((a, b) => a - b) : FALLBACK_YEARS
}

/** Cell key for the draft-string map: "<year>.<field>". */
function cellKey(year: number, field: BalanceSheetField): string {
  return `${year}.${field}`
}

export function BalanceSheetInputs({ dealId, onSaved }: Props) {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [years, setYears] = useState<number[]>(FALLBACK_YEARS)
  // Cells hold the display string, not a number. Same reason as the P&L grid: a
  // controlled numeric input eats a trailing "." or "0" as it is typed, so
  // "1,200.05" would be impossible to enter.
  const [cells, setCells] = useState<Record<string, string>>({})

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await apiClient.get<NormalizationEnvelope>(
        `/qoe/deals/${dealId}/normalization`,
      )
      const extras = res.data.form_extras_json ?? {}
      const fyYears = yearsFrom(res.data.financial_years)
      setYears(fyYears)
      const block = extras.balance_sheet ?? {}
      const next: Record<string, string> = {}
      for (const year of fyYears) {
        const row: BalanceSheetCapturePeriod = block[String(year)] ?? {}
        for (const field of BALANCE_SHEET_FIELDS) {
          const val = row[field]
          next[cellKey(year, field)] = val != null ? numericInputValue(val) : ''
        }
      }
      setCells(next)
    } catch {
      // A deal with no normalization session yet is the ordinary early state,
      // not a failure — the form simply opens empty.
      setYears(FALLBACK_YEARS)
    } finally {
      setLoading(false)
    }
  }, [dealId])

  useEffect(() => { void load() }, [load])

  const setCell = (year: number, field: BalanceSheetField, raw: string) => {
    setCells(prev => ({ ...prev, [cellKey(year, field)]: formatNumericInput(raw) }))
  }

  /** A period counts toward the peg once any of its figures is entered — the
   *  same rule the server applies in _capture.balance_sheet_periods(). */
  const populatedYears = years.filter(y =>
    BALANCE_SHEET_FIELDS.some(f => (cells[cellKey(y, f)] ?? '').trim() !== ''),
  )

  const save = async () => {
    setSaving(true)
    try {
      // Only the balance_sheet block. financial_years / adjustments are omitted
      // on purpose: the save route is preserve-on-omit and form_extras_json is
      // merged rather than replaced, so this cannot disturb the P&L grid or the
      // proof-of-cash figures saved from the other panel.
      await apiClient.put(`/qoe/deals/${dealId}/normalization`, {
        balance_sheet: years.map(y => {
          const row: Record<string, number | null> = { year: y }
          for (const field of BALANCE_SHEET_FIELDS) {
            const parsed = parseNumericInput(cells[cellKey(y, field)] ?? '')
            // '' clears the field: stored as null, which reads as "not entered"
            // rather than a reported zero.
            row[field] = parsed === '' ? null : parsed
          }
          return row
        }),
      })
      toast.success('Balance sheet saved')
      onSaved?.()
    } catch (err) {
      toast.error(formatApiError(err))
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="rounded-xl border border-border bg-card p-3 flex flex-col gap-3">
        <Skeleton className="h-5 w-56" />
        <Skeleton className="h-28 w-full" />
      </div>
    )
  }

  return (
    <div className="rounded-xl border border-border bg-card p-3 flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <ScaleIcon className="h-4 w-4 text-brand-primary" />
        <h2 className="text-sm font-semibold text-brand-text">Balance Sheet</h2>
      </div>

      <p className="text-[11px] leading-relaxed text-muted-foreground">
        Enter these as reported, one column per fiscal year. Net working capital is
        <span className="font-medium text-brand-text"> AR + Inventory − AP − Accrued Liabilities</span>;
        the peg is the average across the periods entered, with a ±10% collar. Cash is
        collected for the equity bridge and is <span className="font-medium text-brand-text">not</span> part
        of the NWC formula. Leave a field blank if it has not been provided — blank is
        not zero.
      </p>

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b text-left text-muted-foreground">
              <th className="py-1 pr-3 font-normal">Line item</th>
              {years.map(y => (
                <th key={y} className="py-1 pr-3 font-normal text-right">FY{y}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {BALANCE_SHEET_FIELDS.map(field => (
              <tr key={field} className="border-b last:border-0">
                <td className="py-1.5 pr-3 whitespace-nowrap">
                  {FIELD_LABELS[field]}
                  {!IN_PEG[field] && (
                    <span className="ml-1.5 text-[10px] text-muted-foreground">(not in NWC)</span>
                  )}
                </td>
                {years.map(y => (
                  <td key={y} className="py-1 pr-3">
                    <Input
                      type="text"
                      inputMode="decimal"
                      value={cells[cellKey(y, field)] ?? ''}
                      onChange={e => setCell(y, field, e.target.value)}
                      aria-label={`FY${y} ${FIELD_LABELS[field]}`}
                      placeholder="—"
                      className="h-7 w-32 ml-auto text-right text-xs tabular-nums"
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between gap-3">
        <p className="text-[11px] text-muted-foreground">
          {populatedYears.length >= MIN_PERIODS
            ? `${populatedYears.length} periods entered — enough for a peg.`
            : `${populatedYears.length} of ${MIN_PERIODS} periods entered. The NWC peg stays pending until ${MIN_PERIODS} are on file.`}
        </p>
        <Button size="sm" onClick={save} disabled={saving} className="h-7 gap-1.5 text-xs">
          {saving && <Loader2Icon className="h-3.5 w-3.5 animate-spin" />}
          Save Balance Sheet
        </Button>
      </div>
    </div>
  )
}

export default BalanceSheetInputs
